import LimitsContainer, { EventTypeHeading } from './LimitsContainer.jsx';
import Modal from '../Modal';
import { useEffect, useState } from 'react';
import Select from '../Select';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline/index.js';
import TextInput from '../TextInput';
import EventTypeCheckboxes from '../EventTypeCheckboxes.jsx';
import { useEventTypesQuery } from '../../lib/Queries';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import Toggle from '~/components/Toggle';
import InfoMessageBlock from '~/components/InfoMessageBlock';

const EventTypeLabel = ({
    setConsumesQuantity,
    maxQuantity,
    isChecked,
    consumesQuantity,
    hasTypeQuantities,
    opt,
    extraTime,
    setExtraTime,
}) => {
    const label = opt.label;
    const hasExtraTime = !!extraTime?.start || !!extraTime?.end;
    const [showExtraTime, setShowExtraTime] = useState(hasExtraTime);

    return (
        <div>
            <div className="flex items-start">
                <div className="flex-1">
                    <div>{label}</div>

                    {hasTypeQuantities && isChecked && (
                        <div className="mt-4">
                            <div className="input-label">Capacity Used</div>
                            <input
                                type="number"
                                className="text-sm !w-16 mr-2"
                                placeholder="1"
                                min="1"
                                max={maxQuantity}
                                autoFocus={!consumesQuantity}
                                value={consumesQuantity}
                                onChange={e => setConsumesQuantity(e.target.value)}
                            />
                        </div>
                    )}

                    {opt.isClass && isChecked && (
                        <Select
                            className="mt-4"
                            inputClassName="max-w-xs"
                            label="When is this used?"
                            value={
                                extraTime?.consumeClassInstance ? 'class' : 'attendee'
                            }
                            onChange={value =>
                                setExtraTime({
                                    ...extraTime,
                                    consumeClassInstance: value === 'class',
                                })
                            }
                            options={[
                                {
                                    value: 'attendee',
                                    label: 'Per attendee — Use for each person who books the class.',
                                },
                                {
                                    value: 'class',
                                    label: 'Per class — Reserve when the class is scheduled, regardless of attendance.',
                                },
                            ]}
                        />
                    )}

                    {hasTypeQuantities && isChecked && (
                        <div className="mt-4">
                            <div className="panel-label">Additional Options</div>
                            <div className="panel py-4 pl-4">
                                <div className="flex items-center justify-between pr-4">
                                    <div>
                                        <div>
                                            Block off time before or after
                                        </div>
                                        <div className="text-gray-500 text-sm">
                                            Reserve this for additional time before or after the booking
                                        </div>
                                        {(hasExtraTime || showExtraTime) && (
                                            <div className="flex mt-2">
                                                <TextInput
                                                    type="number"
                                                    label="Start"
                                                    className="text-sm !w-24 mr-2"
                                                    placeholder="None"
                                                    max={extraTime?.start || ''}
                                                    value={extraTime?.start}
                                                    onChange={value =>
                                                        setExtraTime({
                                                            ...extraTime,
                                                            start: value,
                                                        })
                                                    }
                                                />
                                                <TextInput
                                                    type="number"
                                                    className="text-sm !w-24 mr-2"
                                                    label="End"
                                                    placeholder="None"
                                                    max={extraTime?.end || ''}
                                                    value={extraTime?.end}
                                                    onChange={value =>
                                                        setExtraTime({
                                                            ...extraTime,
                                                            end: value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <Toggle
                                        enabled={showExtraTime || hasExtraTime}
                                        setEnabled={(value) => {
                                            if (value) {
                                                setShowExtraTime(true);
                                            } else {
                                                setShowExtraTime(false);
                                                setExtraTime({})
                                            }
                                        }}
                                    />
                                </div>
                                <div className="mt-4 pt-4 pr-4 border-t flex items-center justify-between">
                                    <div>
                                        Allow booking when capacity is unavailable
                                        <div className="text-gray-500 text-sm">
                                            When this is enabled a booking will consume capacity, but a lack of capacity will not prevent it from being booked
                                        </div>
                                    </div>
                                    <Toggle
                                        enabled={!!extraTime?.onlyConsumes}
                                        setEnabled={value =>
                                            setExtraTime({
                                                ...extraTime,
                                                onlyConsumes: value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ResourceInput = ({ resource, setResource, addResource }) => {
    const settings = resource?.limit;
    const { data: allEventTypes } = useEventTypesQuery();

    const isNewResource = !settings?.name;
    const [isEditing, setIsEditing] = useState(isNewResource);

    function setSettings(newSettings) {
        setResource({ ...resource, limit: newSettings });
    }

    useEffect(() => {
        if (!settings?.quantity) {
            setSettings({ ...settings, quantity: 1 });
        }
    }, []);

    const eventTypeNames = () => {
        return allEventTypes
            ?.filter(t => resource.types?.indexOf(t.id) > -1)
            .map(t => t.name)
            .join(', ');
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center flex-1">
                    {isEditing && (
                        <>
                            <TextInput
                                value={settings?.name || ''}
                                onChange={newValue =>
                                    setSettings({ ...settings, name: newValue })
                                }
                                label="Name"
                                placeholder="ex., Rooms"
                                className="mr-4 min-w-lg"
                            />

                            <TextInput
                                type="number"
                                value={settings?.quantity || ''}
                                onChange={newValue =>
                                    setSettings({
                                        ...settings,
                                        quantity: newValue,
                                    })
                                }
                                label="Available at one time"
                                inputClassName="!w-24"
                            />
                        </>
                    )}
                    {!isEditing && (
                        <div>
                            <span className="font-semibold">
                                {settings?.quantity} {settings?.name}
                            </span>{' '}
                            for{' '}
                            <EventTypeHeading
                                typeIds={resource.types}
                                className="inline-block"
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end items-center">
                    <button
                        className="text-red-600 hover:text-red-700"
                        type="button"
                        onClick={() => setResource(null)}
                    >
                        <TrashIcon className="w-5 h-5" />
                    </button>

                    <button
                        className="black-clickable ml-4"
                        type="button"
                        onClick={() => setIsEditing(!isEditing)}
                    >
                        <PencilSquareIcon className="w-5 h-5" />
                    </button>

                    <button
                        className="black-clickable ml-4"
                        type="button"
                        onClick={addResource}
                    >
                        <PlusIcon className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {isEditing && (
                <div>
                    <div className="flex items-center justify-end text-sm">
                        Advanced mode
                        <Toggle
                            enabled={!!settings?.hasTypeQuantities}
                            setEnabled={isEnabled =>
                                setSettings({
                                    ...settings,
                                    hasTypeQuantities: isEnabled,
                                })
                            }
                            className="ml-2"
                        />
                    </div>
                    <div className="input-label">Which bookings use this?</div>
                    <EventTypeCheckboxes
                        onChange={newTypes =>
                            setResource({ ...resource, types: newTypes })
                        }
                        value={resource.types}
                        renderLabel={({ opt }) => (
                            <EventTypeLabel
                                opt={opt}
                                hasTypeQuantities={
                                    !!settings?.hasTypeQuantities
                                }
                                isChecked={
                                    (resource?.types || []).indexOf(opt.value) >
                                    -1
                                }
                                maxQuantity={settings?.quantity}
                                consumesQuantity={
                                    (settings?.consumesQuantity || {})[
                                        opt.value
                                    ]
                                }
                                setConsumesQuantity={value =>
                                    setSettings({
                                        ...settings,
                                        consumesQuantity: {
                                            ...settings?.consumesQuantity,
                                            [opt.value]: value,
                                        },
                                    })
                                }
                                extraTime={
                                    (settings?.extraTime || {})[opt.value]
                                }
                                setExtraTime={value =>
                                    setSettings({
                                        ...settings,
                                        extraTime: {
                                            ...settings?.extraTime,
                                            [opt.value]: value,
                                        },
                                    })
                                }
                            />
                        )}
                    />
                </div>
            )}
        </div>
    );
};

const ResourceModal = ({ isOpen, onClose, doSave, initialSettings }) => {
    const [resources, setResources] = useState(
        initialSettings?.exceptions || [],
    );
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setResources(initialSettings?.exceptions || []);
        }
    }, [isOpen]);

    async function save() {
        setIsSaving(true);

        try {
            await doSave({
                exceptions: resources,
            });
            onClose();
        } catch (e) {
            // ...
        }

        setIsSaving(false);
    }

    function addResource() {
        setResources([...resources, {}]);
    }

    return (
        <Modal onClose={onClose} isOpen={isOpen}>
            <h2 className="mb-6">Resource Constraints</h2>
            <p className="mb-6">
                Use this when multiple bookings share something limited, like
                rooms, seats, equipment, or space. You don't need this just to
                prevent a person from being booked twice.
            </p>
            <div>
                {resources.length === 0 && (
                    <div className="text-gray-500">
                        There are no room/resource constraints set up
                    </div>
                )}
                {resources.map((resource, idx) => (
                    <ResourceInput
                        key={idx}
                        resource={resource}
                        addResource={addResource}
                        setResource={newValue => {
                            const newResources = [...resources];
                            if (newValue) {
                                newResources[idx] = newValue;
                            } else {
                                newResources.splice(idx, 1);
                            }
                            setResources(newResources);
                        }}
                    />
                ))}
            </div>

            {!resources?.length && (
                <button className="btn-light-pill mt-4" onClick={addResource}>
                    Add Resource
                </button>
            )}

            {(resources?.length > 0 ||
                initialSettings?.exceptions?.length > 0) && (
                <div className="mt-4">
                    <button onClick={save} className="btn-save">
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            )}
        </Modal>
    );
};

const LimitDescription = ({ limit, isDefault, hasExceptions }) => {
    if (isDefault) {
        return (
            <div>
                {hasExceptions && (
                    <div className="panel-label">Resource Constraints</div>
                )}
                {!hasExceptions && (
                    <div>No resource constraints have been set up</div>
                )}
            </div>
        );
    }

    if (!limit) {
        return <div>There are no room/resource constraints</div>;
    }

    const isAdvanced = !!limit?.hasTypeQuantities;

    return (
        <div>
            <span className="font-semibold">{limit.name} </span>
            {limit.quantity} available at one time{' '}
            {isAdvanced ? '(advanced)' : ''}
        </div>
    );
};

const LimitResourceConstraints = ({ limit }) => {
    return (
        <LimitsContainer
            CustomModal={ResourceModal}
            LimitDescription={LimitDescription}
            type="resource"
            limit={limit}
            exceptionsLabel=" "
        />
    );
};

export default LimitResourceConstraints;
